import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useFetch } from 'use-http';
import FlexSearch from 'flexsearch';
import { BlogCardData } from '../../lib/types';

export const query = graphql`
    query BlogSearchQuery {
        search: localSearchBlog {
            publicIndexURL
            publicStoreURL
        }
    }
`;

interface BlogSearchQuery {
    search: {
        publicIndexURL: string;
        publicStoreURL: string;
    };
}

type BlogListSearchResult = BlogCardData & {
    tags: ReadonlyArray<{
        slug: string;
    }>;
    categories: ReadonlyArray<{
        slug: string;
    }>;
};

export default function useSearch(
    // eslint-disable-next-line @typescript-eslint/default-param-last
    searchQuery = '',
    selectedCategory: string | undefined,
    selectedTag: string | undefined,
): ReadonlyArray<BlogListSearchResult> {
    const {
        search: { publicIndexURL, publicStoreURL },
    } = useStaticQuery<BlogSearchQuery>(query);
    const [results, setResults] = React.useState<
        ReadonlyArray<BlogListSearchResult>
    >(() => []);

    const { data: storeData } = useFetch(publicStoreURL, {}, []);
    const { data: indexData } = useFetch(publicIndexURL, {}, []);

    React.useEffect(() => {
        if (!searchQuery || !indexData || !storeData) {
            setResults([]);
            return;
        }

        const importedIndex = FlexSearch.create();
        importedIndex.import(JSON.stringify(indexData));

        const rawResults = importedIndex.search(searchQuery, {
            bool: 'or',
        }) as unknown as string[];
        const blogEntries = rawResults
            .map(
                (id: string) =>
                    (storeData as Record<string, BlogListSearchResult>)[
                        id as string
                    ],
            )
            .filter(
                (blogListEntry) =>
                    (!selectedTag ||
                        selectedTag === 'all' ||
                        blogListEntry.tags?.find(
                            (tag) => tag.slug === selectedTag,
                        )) &&
                    (!selectedCategory ||
                        selectedCategory === 'all' ||
                        blogListEntry.categories?.find(
                            (category) => category.slug === selectedCategory,
                        )),
            );
        setResults(blogEntries);
    }, [searchQuery, storeData, indexData, selectedTag, selectedCategory]);

    return results;
}
